import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle, HeartHandshake, Activity, Syringe, Building2, ClipboardCheck,
  Stethoscope, ShieldCheck, GraduationCap, ScrollText, PenLine, Sparkles, X as XIcon,
} from "lucide-react";

const OPTIONS = {
  transplant_status: [
    { v: "Not Evaluated",         color: "#A3ACA7", hint: "Baseline — has not entered transplant workup." },
    { v: "Evaluating",            color: "#60A5FA", hint: "Multi-disciplinary transplant workup underway." },
    { v: "Waitlisted",            color: "#4ADE80", hint: "On the UNOS / national deceased-donor list." },
    { v: "Pre-emptive Scheduled", color: "#22D3EE", hint: "Living-donor transplant scheduled before dialysis." },
    { v: "Ineligible",            color: "#F87171", hint: "Not a transplant candidate — comorbidity or contraindication." },
  ],
  dialysis_modality_choice: [
    { v: "Undecided",               color: "#A3ACA7", hint: "Patient still weighing options — refer to modality education." },
    { v: "Hemodialysis (HD)",       color: "#60A5FA", hint: "In-center or home HD; requires vascular access." },
    { v: "Peritoneal Dialysis (PD)", color: "#22D3EE", hint: "Home-based; requires PD catheter placement 2 weeks before start." },
    { v: "Conservative Care",       color: "#FBBF24", hint: "Non-dialytic; align with palliative care team." },
  ],
  vascular_access_status: [
    { v: "Not Referred",           color: "#A3ACA7", hint: "No vascular-surgery consult scheduled yet." },
    { v: "Referred to Surgeon",    color: "#60A5FA", hint: "Consult pending or scheduled." },
    { v: "Fistula/Graft Maturing", color: "#FBBF24", hint: "Access created; maturing (typical 6-8 weeks for AVF)." },
    { v: "Ready for Use",          color: "#4ADE80", hint: "Access cannulated / ready — safe to initiate HD." },
  ],
};

const TOGGLE_FIELDS = [
  { key: "immunizations_complete",    label: "Immunizations complete",     icon: Syringe,
    hint: "Hep B, pneumococcal, influenza, COVID-19, VZV — transplant-list prerequisite." },
  { key: "education_completed",       label: "Modality education completed", icon: GraduationCap,
    hint: "Patient (± caregiver) completed the HD vs PD vs conservative-care decision aid." },
  { key: "advance_directive_on_file", label: "Advance directive on file",  icon: ScrollText,
    hint: "Written goals of care / DNR / power of attorney signed and scanned." },
];

const OPTION_META = {
  transplant_status:        { title: "Transplant status",        icon: HeartHandshake, testId: "care-transplant" },
  dialysis_modality_choice: { title: "Dialysis modality choice", icon: Activity,       testId: "care-modality" },
  vascular_access_status:   { title: "Vascular access status",   icon: Stethoscope,    testId: "care-vascular" },
};

/**
 * "Human-in-the-Loop" suggestion row. Rendered whenever the current stored
 * value is at its default AND the backend produced a data-driven suggestion.
 * Two actions: Approve (accepts the suggestion, audit-logged as
 * approved_ai_suggestion) or Override (dismisses the banner and lets the
 * doctor pick manually below).
 */
function AISuggestionBanner({ fieldKey, suggestion, label, onApprove, onOverride, disabled }) {
  if (!suggestion) return null;
  const displayValue = typeof suggestion.value === "boolean"
    ? (suggestion.value ? "Yes" : "No")
    : suggestion.value;
  return (
    <div className="rounded-xl border border-[#60A5FA]/40 bg-gradient-to-r from-[#60A5FA]/10 to-[#60A5FA]/0 p-3 mb-3"
         data-testid={`ai-suggestion-${fieldKey}`}>
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-lg bg-[#60A5FA]/25 border border-[#60A5FA]/40 flex items-center justify-center shrink-0 mt-0.5">
          <Sparkles size={12} className="text-[#60A5FA]" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#60A5FA]">AI Suggests</div>
          <div className="text-sm font-semibold text-white mt-0.5">
            {label ? `${label}: ` : ""}<span style={{ color: "#60A5FA" }}>{displayValue}</span>
          </div>
          <div className="text-[11px] text-[#A3ACA7] mt-1 leading-snug">{suggestion.reasoning}</div>
          {suggestion.source && (
            <div className="text-[10px] text-[#7A8480] mt-1 italic">Source: {suggestion.source}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={onApprove}
          disabled={disabled}
          data-testid={`ai-approve-${fieldKey}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#60A5FA] text-black text-xs font-bold hover:bg-[#60A5FA]/90 disabled:opacity-60"
        >
          <ShieldCheck size={12} /> Approve
        </button>
        <button
          type="button"
          onClick={onOverride}
          disabled={disabled}
          data-testid={`ai-override-${fieldKey}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#272E2B] text-[#A3ACA7] text-xs font-semibold hover:text-white hover:border-[#60A5FA]/40 disabled:opacity-60"
        >
          <XIcon size={12} /> Override — pick manually
        </button>
      </div>
    </div>
  );
}

function StatusPicker({ field, current, onChange, disabled, suggestion, onApprove, showManual, onDismissSuggestion }) {
  const meta = OPTION_META[field];
  const Icon = meta.icon;
  const bannerActive = suggestion && !showManual;
  return (
    <div className="ros-card" data-testid={`${meta.testId}-card`}>
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#60A5FA]/10 border border-[#60A5FA]/30 flex items-center justify-center">
          <Icon size={16} className="text-[#60A5FA]" />
        </div>
        <div>
          <div className="font-heading text-white text-sm">{meta.title}</div>
          <div className="text-[11px] text-[#A3ACA7]">Select the current stage</div>
        </div>
      </div>

      <div className="mt-4">
        {bannerActive && (
          <AISuggestionBanner
            fieldKey={field}
            suggestion={suggestion}
            label={meta.title}
            disabled={disabled}
            onApprove={onApprove}
            onOverride={onDismissSuggestion}
          />
        )}
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${bannerActive ? "opacity-70" : ""}`}>
          {OPTIONS[field].map((opt) => {
            const active = current === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                disabled={disabled}
                onClick={() => onChange(opt.v)}
                data-testid={`${meta.testId}-${opt.v.replace(/[\s\(\)/]+/g, "-")}`}
                className={`text-left px-3 py-2 rounded-lg text-xs border transition-colors ${
                  active
                    ? "bg-white/5 border-2"
                    : "bg-[#1C201E] border-[#272E2B] hover:border-[#60A5FA]/40"
                } disabled:opacity-60 disabled:cursor-not-allowed`}
                style={active ? { borderColor: opt.color, background: `${opt.color}12` } : {}}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: opt.color }} />
                  <span className={`font-semibold ${active ? "text-white" : "text-white/85"}`}>{opt.v}</span>
                </div>
                <div className="text-[10px] text-[#A3ACA7] mt-1 leading-snug">{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ field, checked, onChange, disabled }) {
  const Icon = field.icon;
  return (
    <label className={`flex items-start gap-3 p-4 rounded-xl border transition-colors cursor-pointer ${
      checked ? "border-[#4ADE80]/40 bg-[#4ADE80]/8" : "border-[#272E2B] bg-[#1C201E]"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-[#4ADE80]/40"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={`toggle-${field.key}`}
        className="mt-1 accent-[#4ADE80]"
      />
      <Icon size={16} className={checked ? "text-[#4ADE80] mt-0.5" : "text-[#A3ACA7] mt-0.5"} />
      <div className="flex-1">
        <div className={`font-semibold text-sm ${checked ? "text-white" : "text-white/85"}`}>{field.label}</div>
        <div className="text-[11px] text-[#A3ACA7] mt-0.5 leading-snug">{field.hint}</div>
      </div>
    </label>
  );
}

/**
 * Advanced Care Planning — Provider-facing structured clinical checklist for
 * ESKD & transplant transition. Aligned with KDIGO recommendation to initiate
 * planning once eGFR ≤ 20 or KFRE 2-yr ≥ 20%.
 */
export default function AdvancedCarePlanning() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState(null);
  const [plan, setPlan] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  // Fields the doctor chose to Override — hide their AI banner but keep the manual picker
  const [dismissed, setDismissed] = useState({});
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/care-plan");
      setContext(data.context);
      setPlan(data.care_transition);
      setSuggestions(data.ai_suggestions || {});
      setNotes(data.care_transition?.notes || "");
    } catch (err) {
      toast.error("Could not load care plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (partial) => {
    setSaving(true);
    try {
      const { data } = await api.put("/provider/care-plan", partial);
      setPlan(data.care_transition);
      toast.success(partial.approved_ai_field ? "Suggestion approved" : "Care plan updated");
      // Refresh suggestions since state may have shifted (fields updated → suggestions retract)
      const fresh = await api.get("/care-plan");
      setSuggestions(fresh.data.ai_suggestions || {});
      setContext(fresh.data.context);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const approveSuggestion = (field) => {
    const s = suggestions[field];
    if (!s) return;
    patch({
      [field]: s.value,
      approved_ai_field: field,
      approved_ai_reasoning: s.reasoning,
    });
  };
  const dismissSuggestion = (field) => setDismissed((d) => ({ ...d, [field]: true }));

  const triggered = plan?.eskd_prep_triggered === true;
  const readyForHD = plan?.dialysis_modality_choice === "Hemodialysis (HD)"
                     && plan?.vascular_access_status !== "Ready for Use";

  const progress = useMemo(() => {
    if (!plan) return 0;
    const criteria = [
      plan.eskd_prep_triggered,
      plan.transplant_status !== "Not Evaluated",
      plan.dialysis_modality_choice !== "Undecided",
      plan.vascular_access_status !== "Not Referred",
      plan.immunizations_complete,
      plan.education_completed,
      plan.advance_directive_on_file,
    ];
    return Math.round((criteria.filter(Boolean).length / criteria.length) * 100);
  }, [plan]);

  if (loading) {
    return <div className="p-10 text-[#A3ACA7]" data-testid="care-plan-loading">Loading care plan…</div>;
  }

  return (
    <div className="space-y-6" data-testid="care-plan-page">
      <header className="border-b border-[#272E2B] pb-4">
        <div className="flex items-center gap-3">
          <ClipboardCheck size={26} className="text-[#60A5FA]" />
          <h2 className="font-heading text-2xl">Advanced Care Planning</h2>
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#60A5FA]/10 text-[#60A5FA] border border-[#60A5FA]/30">
            KDIGO · ESKD Transition
          </span>
        </div>
        <p className="text-sm text-[#A3ACA7] mt-2 max-w-3xl">
          KDIGO recommends starting ESKD & transplant preparation once eGFR falls to 20 or when
          KFRE 2-year risk crosses 20%. Every field on this checklist is timestamped and
          audit-logged with your name for medico-legal defensibility.
        </p>
      </header>

      {/* Context header — eGFR + KFRE reason */}
      <div className="ros-card" data-testid="care-context">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="ros-label">Patient</div>
            <div className="font-heading text-lg text-white mt-1">{context?.patient_name}</div>
            <div className="text-xs text-[#A3ACA7]">CKD stage {context?.ckd_stage}</div>
          </div>
          <div>
            <div className="ros-label">Latest eGFR</div>
            <div className="flex items-baseline gap-1 mt-1">
              <div className="ros-metric text-3xl"
                   style={{ color: (context?.latest_egfr ?? 999) <= 20 ? "#F87171" : "#4ADE80" }}>
                {context?.latest_egfr?.toFixed(0) ?? "—"}
              </div>
              <div className="text-xs text-[#A3ACA7]">mL/min/1.73m²</div>
            </div>
          </div>
          <div>
            <div className="ros-label">KFRE · 2 year</div>
            <div className="flex items-baseline gap-1 mt-1">
              <div className="ros-metric text-3xl"
                   style={{ color: (context?.kfre_2yr ?? 0) >= 20 ? "#F87171" : "#FBBF24" }}>
                {context?.kfre_2yr != null ? `${context.kfre_2yr.toFixed(0)}%` : "—"}
              </div>
            </div>
            <div className="text-[11px] text-[#A3ACA7] mt-1">5-yr: {context?.kfre_5yr != null ? `${context.kfre_5yr.toFixed(0)}%` : "—"}</div>
          </div>
          <div>
            <div className="ros-label">Checklist progress</div>
            <div className="ros-metric text-3xl mt-1" style={{ color: "#60A5FA" }}>{progress}%</div>
            <div className="w-full h-1.5 bg-[#272E2B] rounded-full mt-2 overflow-hidden">
              <div className="h-full transition-all"
                   style={{ width: `${progress}%`, background: "#60A5FA" }} />
            </div>
          </div>
        </div>

        {/* KDIGO trigger banner */}
        {context?.trigger_reason && !triggered && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-5 rounded-xl border border-[#F87171]/40 bg-[#F87171]/8 p-4 flex items-start gap-3"
                      data-testid="care-eskd-trigger-banner">
            <AlertTriangle size={22} className="text-[#F87171] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading text-white">KDIGO trigger met</div>
              <div className="text-sm text-white/85 mt-1">{context.trigger_reason}</div>
              <div className="text-xs text-[#A3ACA7] mt-2">
                Toggle <span className="text-white font-semibold">ESKD Prep Started</span> below once you have discussed transition with the patient.
              </div>
            </div>
            <button
              onClick={() => suggestions.eskd_prep_triggered
                ? approveSuggestion("eskd_prep_triggered")
                : patch({ eskd_prep_triggered: true })}
              disabled={saving}
              data-testid="care-acknowledge-trigger"
              className="text-xs font-semibold text-[#F87171] border border-[#F87171]/50 hover:bg-[#F87171] hover:text-black transition-colors px-3 py-1.5 rounded-full"
            >
              Acknowledge & begin prep
            </button>
          </motion.div>
        )}

        {triggered && (
          <div className="mt-5 rounded-xl border border-[#4ADE80]/40 bg-[#4ADE80]/8 p-4 flex items-start gap-3"
               data-testid="care-eskd-active-banner">
            <ShieldCheck size={22} className="text-[#4ADE80] shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-white">ESKD preparation active</div>
              <div className="text-xs text-[#A3ACA7] mt-1">
                Last updated by <span className="text-white font-semibold">{plan.updated_by_name || "—"}</span>
                {plan.updated_at && <> on {plan.updated_at.slice(0, 10)}</>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Warning: HD chosen but no access ready */}
      {triggered && readyForHD && plan?.vascular_access_status === "Not Referred" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="ros-card !border-[#FBBF24]/40 !bg-[#FBBF24]/8"
                    data-testid="care-warn-no-access">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#FBBF24] mt-0.5" />
            <div>
              <div className="font-heading text-white">Missing vascular-access referral</div>
              <div className="text-xs text-[#A3ACA7] mt-1">
                Patient elected Hemodialysis but no vascular-surgery consult is scheduled.
                AVF creation & maturation typically requires 6-8 weeks — refer promptly.
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Structured pickers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["transplant_status", "dialysis_modality_choice", "vascular_access_status"].map((field) => (
          <StatusPicker
            key={field}
            field={field}
            current={plan?.[field]}
            disabled={saving}
            onChange={(v) => patch({ [field]: v })}
            suggestion={!dismissed[field] ? suggestions[field] : null}
            onApprove={() => approveSuggestion(field)}
            onDismissSuggestion={() => dismissSuggestion(field)}
          />
        ))}
      </div>

      {/* Toggles */}
      <div className="ros-card">
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={18} className="text-[#4ADE80]" />
          <div className="font-heading text-white">Clinical readiness checklist</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TOGGLE_FIELDS.map((f) => (
            <div key={f.key}>
              {!dismissed[f.key] && suggestions[f.key] && (
                <AISuggestionBanner
                  fieldKey={f.key}
                  suggestion={suggestions[f.key]}
                  label={f.label}
                  disabled={saving}
                  onApprove={() => approveSuggestion(f.key)}
                  onOverride={() => dismissSuggestion(f.key)}
                />
              )}
              <ToggleRow field={f} disabled={saving}
                         checked={!!plan?.[f.key]}
                         onChange={(val) => patch({ [f.key]: val })} />
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="ros-card" data-testid="care-notes-card">
        <div className="flex items-center gap-2 mb-3">
          <PenLine size={16} className="text-[#60A5FA]" />
          <div className="font-heading text-white">Care coordination notes</div>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => { if (notes !== (plan?.notes || "")) patch({ notes }); }}
          rows={4}
          placeholder="Handoff notes for the transplant coordinator or dialysis team…"
          data-testid="care-notes-input"
          className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#60A5FA]"
        />
        <div className="text-[11px] text-[#A3ACA7] mt-2">Auto-saves when you tab out of the field. Every change is audit-logged.</div>
      </div>
    </div>
  );
}
